// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { isEmpty, isNil, some } from 'lodash/fp';
import constants from 'logic/data/Izenda.CopyManagementConstants';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import connect from 'share/enhancers/Izenda.Connect';
import commonMessages from 'share/messages/Izenda.CommonMessages';
import ActionBar from './ActionBar/Izenda.ActionBar';
import SaveWorkspacePopup from './ActionBar/Izenda.SaveWorkspacePopup';
import AdditionalItems from './AdditionalItems/Izenda.AdditionalItems';
import AdvancedCopy from './AdvancedCopy/Izenda.AdvancedCopy';
import HeaderSection from './HeaderSection/Izenda.HeaderSection';
import RolesCopySetting from './RolesCopy/Izenda.RolesCopySetting';
import SystemConfigurationCopySetting from './SystemConfigurationCopy/Izenda.SystemConfigurationCopySetting';
import RunCopyPopup from './RunCopy/Izenda.RunCopyPopup';
import SourceTree from './RunCopy/Izenda.SourceTree';
import RunCopyTree from './RunCopy/Tree/Izenda.RunCopyTree';
import SourceDestinationSettings from './SourceDestination/Izenda.SourceDestinationSettings';
import WorkspaceSchedulePopup from './Schedule/Izenda.WorkspaceSchedulePopup';
import WorkspaceDetailStore from 'logic/Izenda.WorkspaceDetailStore';
import WorkspaceDetailModel from 'logic/models/Izenda.WorkspaceDetail';

/**
 * @module WorkspaceDetail component
 * @example
 *   <WorkspaceDetail>
 *     <ActionBar></ActionBar>
 *     <HeaderSection></HeaderSection>
 *     <AdditionalItems></AdditionalItems>
 *     <SourceDestinationSettings></SourceDestinationSettings>
 *   </WorkspaceDetail>
 */
class WorkspaceDetail extends Component {
  propTypes: {
    store: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired
  };

  componentDidMount() {
    this.props.store.init();
  }

  componentWillUnmount() {
    this.props.store.destroy();
  }

  componentWillReceiveProps(nextProps: any) {
    const { loader, store, item, validation } = this.props;

    if (item !== nextProps.item) {
      item.resetSelectedTenant();
      validation.reset();
      item.setAlertMessage(commonMessages.EMPTY_MESSAGE);
      if (nextProps.item.id) {
        loader.startLoading();
        return store
          .load(nextProps.item.id)
          .then(store.loadRolesAndPermissions.bind(store, nextProps.item.sourceId))
          .finally(loader.endLoading.bind(loader));
      }
    }
  }

  onOpenPopup = (isSaveAs: boolean) => {
    return this.props.openPopup(SaveWorkspacePopup, {
      onClosing: this.onSave,
      store: this.props.store,
      isSaveAs
    });
  };

  onSave = (isSaveAs: boolean) => {
    const {
      item,
      item: { canSave, isNew },
      store,
      localization
    } = this.props;

    if (canSave || isSaveAs) {
      // handle concurrency when save old item
      // because when save as or save new item it had already handled in Save Workspace pop up
      return store.save(isSaveAs).then(result => {
        if (!isNew && !isSaveAs) {
          return this.reloadDataPopup(result);
        }
        return result;
      });
    } else {
      item.alertMessage = {
        type: commonMessages.MESSAGE_TYPE.INFO,
        message: localization.getText('COMMON_MESSAGES_NO_CHANGES_FOUND')
      };
      return Promise.resolve();
    }
  };

  render() {
    // Workspace detail store
    const {
      item,
      item: { selectedTenant, visible, alertMessage, copyRoles, copySystemConfiguration, copyOnlySettings },
      store
    } = this.props;

    return selectedTenant ? (
      <AdvancedCopy store={store} />
    ) : (
      <section className='izenda-Wrapper'>
        <ActionBar
          item={item}
          onCancel={this.onCancel}
          onSave={this.onOpenSavePopup}
          onSaveAs={this.onOpenSaveAsPopUp}
          onRefresh={this.onRefresh}
          onSchedule={this.onSchedule}
          onRunCopy={this.onRunCopy}
          onValidate={this.onValidate}
          disabled={!visible}
          alertMessage={alertMessage}
        />

        <div className='izenda-Wrapper-content'>
          <ScrollBox>
            <div className='u_paddingLeftRight'>
              <HeaderSection store={store} item={item} />
              <AdditionalItems store={store} item={item} />

              <SourceDestinationSettings store={store} item={item} />
              {!copyOnlySettings && copyRoles && <RolesCopySetting store={store} id={constants.MODULE_ANCHORS.ROLE_SETTING} />}
              {!copyOnlySettings && copySystemConfiguration && <SystemConfigurationCopySetting store={store} id={constants.MODULE_ANCHORS.ROLE_SETTING} />}
            </div>
          </ScrollBox>
        </div>
      </section>
    );
  }

  /**
   * Retrieve connection detail and update via API.
   */
  onOpenSavePopup = () => {
    const {
      item: { isNew },
      loader
    } = this.props;

    if (isNew) {
      return this.onOpenPopup(false);
    } else {
      loader.startLoading();
      return this.onSave(false).finally(loader.endLoading.bind(loader));
    }
  };

  /**
   * open SaveAs popup
   */
  onOpenSaveAsPopUp = () => {
    this.onOpenPopup(true);
  };

  /**
   * Cancel changes that user made for workspace.
   */
  onCancel = () => {
    const {
      loader,
      item: { isNew, id },
      store
    } = this.props;
    if (isNew) {
      store.masterStore.filter = '';
      store.masterStore.addWorkspace();
    } else {
      loader.startLoading();
      store.load(id).finally(loader.endLoading.bind(loader));
    }
  };

  /**
   * refresh workspace detail
   */
  onRefresh = () => {
    const { loader, store } = this.props;

    loader.startLoading();
    store.checkHashCodeSource().finally(loader.endLoading.bind(loader));
  };

  /**
   * Open schedule dialog
   */
  onSchedule = () => {
    const { props } = this;
    const store: WorkspaceDetailStore = props.store;
    const workspaceDetail: WorkspaceDetailModel = store && store.item;
    return props.openPopup(WorkspaceSchedulePopup, {
      workspaceDetail: workspaceDetail
    });
  };

  /**
   * Run copy selected sources
   */
  onRunCopy = () => {
    const {
      store: { item }
    } = this.props;

    item.resetValueForRunCopy();
    item.setCanRunCopyStatus(false);
    this.onValidate().then(result => {
      result && result.success && this.handleOverwriteConfirmation();
      return null;
    });
  };

  /**
   * check Result After Validate
   * @param  {Array<Object>} results
   * @return {boolean}
   */
  checkResultAfterValidate(results: Array<any>): boolean {
    return some(item => item.success, results);
  }

  /**
   * handle Overwrite Confirmation
   */
  handleOverwriteConfirmation = () => {
    const {
      store,
      store: {
        item: {
          copyDataModel,
          copyAdvancedSettings,
          copyDashboard,
          copyReport,
          copyTenantPermissions,
          copyRolePermission
        }
      }
    } = this.props;

    const cancelCopy = function() {
      return null;
    };

    return Promise.resolve()
      .then(() => {
        // if checkbox copy data model checked
        // then call api to get the conflicting data model need to confirm overwrite
        // if cancel copy data model then continue to run copy with the others or cancel Run Copy
        if (copyDataModel) {
          return store
            .checkConnectionHasDataModel()
            .then(this.handleRunCopyDataModel)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(() => {
        // if checkbox copy advanced settings checked
        // then call api to get the conflicting advanced settings need to confirm overwrite
        // if cancel copy advanced settings then continue to run copy with the others or cancel Run Copy
        if (copyAdvancedSettings) {
          return store
            .detectConflictingAdvancedSettings()
            .then(this.handleRunCopyAdvancedSetting)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(() => {
        // if checkbox copy dashboard checked
        // then call api to get the conflicting dashboards need to confirm overwrite
        // if cancel copy reports then continue to run copy with the others or cancel Run Copy
        if (copyDashboard) {
          return store
            .detectConflictingDashboards()
            .then(this.handleRunCopyDashboard)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(() => {
        // if checkbox copy report checked
        // then call api to get the conflicting reports need to confirm overwrite
        // if cancel copy reports then continue to run copy with the others or cancel Run Copy
        if (copyReport) {
          return store
            .detectConflictingReports()
            .then(this.handleRunCopyReport)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(() => {
        // if checkbox copy report checked
        // then call api to get the conflicting advanced settings need to confirm overwrite
        // if cancel copy advanced settings then continue to run copy with the others or cancel Run Copy
        if (copyReport) {
          return store
            .detectConflictingTemplates()
            .then(this.handleRunCopyTemplate)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(() => {
        // if checkbox copy tenant permissions checked
        // then call api to get the conflicting tenant permissions need to confirm overwrite
        // if cancel copy tenant permissions then continue to run copy with the others or cancel Run Copy
        if (copyTenantPermissions) {
          return store
            .detectConflictingTenantPermissions()
            .then(this.handleRunCopyTenantPermissions)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(() => {
        // if checkbox copy role permissions checked
        // then call api to get the conflicting role permissions need to confirm overwrite
        // if click cancel then cancel Run Copy
        if (copyRolePermission) {
          return store
            .detectConflictingRolePermission()
            .then(this.handleRunCopyRolePermissions)
            .catch(cancelCopy);
        }
        return null;
      })
      .then(this.handleRunCopy);
  };

  /**
   * handle Run Copy
   * @return {promise}
   */
  handleRunCopy = () => {
    const props = this.props;
    const {
      validation: valManager,
      store,
      store: { item },
      loader,
      localization
    } = props;

    // do not execute run copy
    if (!item.isReadyToRunCopy) {
      item.showAlertMessage('info', localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_NO_RUN_COPY_INFO_MESSAGE'));
      return null;
    }

    // Execute Run copy
    loader.startLoading();
    return store
      .runCopy()
      .then(result => {
        if (result && !isEmpty(result.messages)) {
          valManager.updateErrors(result.messages);
        }

        if (result && result.data && result.data.hadCopiedFilterLookup) {
          return props.confirmFn('Information', {
            type: 'info',
            description: constants.COPY_FILTER_LOOKUP_MESSAGE,
            showCloseIndicator: true
          });
        }
      })
      .finally(loader.endLoading.bind(loader));
  };

  /**
   * handle Run Copy Data Model
   * @param  {object} result
   */
  handleRunCopyDataModel = (result: any) => {
    if (isNil(result)) {
      return null;
    }
    const props = this.props;
    const {
      store,
      store: { item },
      localization
    } = props;

    if (result.showRunCopyDataModelPopup) {
      return props
        .openPopup(RunCopyPopup, {
          title: localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_DATA_MODEL_OVERWRITE_CONFIRMATION'),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_DATA_MODEL_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_DATA_MODEL_CONFIRMATION'
          ),
          treeComponent: SourceTree,
          tree: result.tree,
          store
        })
        .then(() => {
          // count item selected in data source tree
          // if have no item selected then cancel Run Copy
          item.getDataModelForRunCopy('copyOption');
          item.setCanRunCopyStatus(true);
        })
        .catch(() => {
          // if cancel then set value overwrite false
          item.getDataModelForRunCopy('copyOption', false, false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Run Copy Advanced Setting
   * @param  {object} result
   */
  handleRunCopyAdvancedSetting = (result: any) => {
    if (isNil(result)) {
      return null;
    }

    const props = this.props;
    const {
      store,
      store: { item },
      localization
    } = props;

    if (result.showRunCopyAdvancedSettingsPopup) {
      return props
        .openPopup(RunCopyPopup, {
          props,
          title: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ADVANCED_DATA_SETTING_OVERWRITE_CONFIRMATION'
          ),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_ADVANCED_SETTING_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_ADVANCED_SETTING_CONFIRMATION'
          ),
          treeComponent: SourceTree,
          tree: result.tree,
          store: store
        })
        .then(() => {
          // count item selected in data source tree
          // if have no item selected then cancel Run Copy
          item.getDataModelForRunCopy('copyAdvancedSettingOption', true);
          item.setCanRunCopyStatus(true);
        })
        .catch(() => {
          // if cancel then set value overwrite false
          item.getDataModelForRunCopy('copyAdvancedSettingOption', true, false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Run Copy Dashboard
   * @param  {object} result
   */
  handleRunCopyDashboard = (result: any) => {
    if (isNil(result)) {
      return null;
    }

    const props = this.props;
    const {
      store: { item },
      localization
    } = props;

    if (result.showRunCopyDashboardPopup) {
      return props
        .openPopup(RunCopyPopup, {
          props,
          title: localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_DASHBOARD_OVERWRITE_CONFIRMATION'),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_DASHBOARD_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_DASHBOARD_CONFIRMATION'
          ),
          treeComponent: RunCopyTree,
          tree: result.tree,
          leafKey: 'dashboards'
        })
        .then(() => {
          item.getDataDashboardsForRunCopy();
          item.setCanRunCopyStatus(true);
        })
        .catch(() => {
          // if cancel then set value overwrite false
          item.getDataDashboardsForRunCopy(false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Run Copy Report
   * @param  {object} result
   */
  handleRunCopyReport = (result: any) => {
    if (isNil(result)) {
      return null;
    }

    const props = this.props;
    const {
      store: { item },
      localization
    } = props;

    if (result.showRunCopyReportPopup) {
      return props
        .openPopup(RunCopyPopup, {
          title: localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_REPORT_OVERWRITE_CONFIRMATION'),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_REPORT_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_REPORT_CONFIRMATION'
          ),
          treeComponent: RunCopyTree,
          tree: result.tree,
          leafKey: 'reports'
        })
        .then(() => {
          item.getDataReportsForRunCopy('copyReportOption');
          item.setCanRunCopyStatus(true);
        })
        .catch(() => {
          // if cancel then set value overwrite false
          item.getDataReportsForRunCopy('copyReportOption', false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Run Copy Report
   * @param  {object} result
   */
  handleRunCopyTemplate = (result: any) => {
    if (isNil(result)) {
      return null;
    }

    const props = this.props;
    const {
      store: { item },
      localization
    } = props;

    if (result.showRunCopyTemplatePopup) {
      return props
        .openPopup(RunCopyPopup, {
          props,
          title: localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_TEMPLATE_OVERWRITE_CONFIRMATION'),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_TEMPLATE_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_TEMPLATE_CONFIRMATION'
          ),
          treeComponent: RunCopyTree,
          tree: result.tree,
          leafKey: 'reports'
        })
        .then(() => {
          item.getDataReportsForRunCopy('copyTemplateOption');
          item.setCanRunCopyStatus(true);
        })
        .catch(() => {
          // if cancel then set value overwrite false
          item.getDataReportsForRunCopy('copyTemplateOption', false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Run Copy Tenant Permissions
   * @param  {object} result
   */
  handleRunCopyTenantPermissions = (result: any) => {
    if (isNil(result)) {
      return null;
    }

    const props = this.props;
    const {
      store,
      store: { item },
      localization
    } = props;

    if (result.showRunCopyTenantPermissionPopup) {
      return props
        .openPopup(RunCopyPopup, {
          props,
          title: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_TENANT_PERMISSIONS_OVERWRITE_CONFIRMATION'
          ),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_TENANT_PERMISSIONS_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_TENANT_PERMISSIONS_CONFIRMATION'
          ),
          treeComponent: SourceTree,
          tree: result.tree,
          store
        })
        .then(() => {
          // count item selected in data source tree
          // if have no item selected then cancel Run Copy
          item.getDataModelForRunCopy('copyTenantPermissionOption', true);
          item.setCanRunCopyStatus(true);
        })
        .catch(() => {
          // if cancel then set value overwrite false
          item.getDataModelForRunCopy('copyTenantPermissionOption', true, false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Run Copy Role Permissions
   * @param  {object} result
   */
  handleRunCopyRolePermissions = (result: any) => {
    if (isNil(result)) {
      return null;
    }

    const props = this.props;
    const {
      store,
      store: { item },
      localization
    } = props;

    if (result.showRunCopyRolePermissionPopup) {
      return props
        .openPopup(RunCopyPopup, {
          props,
          title: localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ROLE_PERMISSIONS_OVERWRITE_CONFIRMATION'),
          informationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_ROLE_PERMISSIONS_INFORMATION'
          ),
          confirmationMessage: localization.getText(
            'COPY_MANAGEMENT_WORKSPACE_DETAIL_RUN_COPY_OVERWRITE_CONFIRM_ROLE_PERMISSIONS_CONFIRMATION'
          ),
          treeComponent: SourceTree,
          tree: result.tree,
          store
        })
        .then(function() {
          // count item selected in data source tree
          // if have no item selected then cancel Run Copy
          item.getDataModelForRunCopy('copyRolePermissionOption');
          item.setCanRunCopyStatus(true);
        })
        .catch(function() {
          // if cancel then set value overwrite false
          item.getDataModelForRunCopy('copyRolePermissionOption', false, false);
        });
    } else {
      this.handleErrorRunCopy(result);
    }
  };

  /**
   * handle Error RunCopy
   * @param  {promise} result
   */
  handleErrorRunCopy = (result: any) => {
    this.handleError(result);
    this.reloadDataPopup(result);
  };

  /**
   * validate required field
   */
  onValidate = () => {
    const {
      store,
      store: { item },
      loader,
      validation: valManager
    } = this.props;
    let showPopupConcurrency = false; // show pop up concurrency at the first time
    item.setAlertMessage(commonMessages.EMPTY_MESSAGE);

    // handle Error After Validate then return result
    const handleErrorAfterValidate = (result: any) => {
      // handle error after bind respond of each promise
      this.handleError(result);
      // Check have concurrency or not
      if (!isEmpty(result.messages) && isEmpty(result.messages[0].key)) {
        if (
          result.messages[0].detail &&
          !showPopupConcurrency
        ) {
          this.reloadDataPopup(result);
          showPopupConcurrency = true;
        } else {
          // Concurrency of Destination connection
          loader.startLoading();
          store.handleAfterConcurrency(result.messages[0].detail).finally(loader.endLoading.bind(loader));
        }
      }
      return result;
    };

    // handle validate
    const handleValidate = (success: boolean) => {
      const successObject = { success: true };
      if (success) {
        loader.startLoading();
        // Loop every workspace tenants to validate
        if (item.copyDataModel || item.copyReport || item.copyDashboard) {
          let tenantArr=item.workspaceTenants.map(tenant=> tenant);
          return Promise.map(tenantArr, (tenant: any) =>
            store.validateWorkspaceTenant(tenant, false).then(handleErrorAfterValidate)
          )
            .then((results: any) => {
              // results is an array of return result of all promise(s)
              if (this.checkResultAfterValidate(results)) {
                return Promise.resolve(successObject);
              }
              return null;
            })
            .finally(loader.endLoading.bind(loader));
        } else {
          return Promise.resolve(successObject).finally(loader.endLoading.bind(loader));
        }
      }
    };
    // validate UI
    return valManager.validate().then(handleValidate);
  };

  /**
   * handle Error
   * @param  {object} result
   */
  handleError = (result: any) => {
    const valManager = this.props.validation;
    if (!isEmpty(result.messages) && !isEmpty(result.messages[0].key)) {
      valManager.updateErrors(result.messages);
    }
  };

  /**
   * reload Data Popup
   * @param  {object} result
   */
  reloadDataPopup = (result: any) => {
    const props = this.props;
    const { store, loader } = props;

    if (!isEmpty(result.messages) && isEmpty(result.messages[0].key)) {
      return props
        .confirmFn('Confirmation', {
          description: result.messages[0].messages[0],
          showCancel: false
        })
        .then(function() {
          loader.startLoading();
          return store.handleAfterConcurrency(result.messages[0].detail).finally(loader.endLoading.bind(loader));
        });
    }

    return result;
  };
}

export default observer(connect()(WorkspaceDetail));



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/CopyManagement/WorkspaceDetail/Izenda.WorkspaceDetail.js